import { useParams } from 'react-router-dom';

import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import useFetchEventOverviewStats, { EStatKeys } from '../helpers/use-fetch-event-overview-stats';
import IconFilterAltGradient from '@ui/icons/gradient-icons/icon-filter-alt-gradient';
import IconSendGradient from '@ui/icons/gradient-icons/icon-send-gradient';
import { getPublishedStatus } from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import { EProjectStatus } from '@/types/projects.types';
import StatsCard from '../components/stats-card';
import Link from '@ui/links/link';
import { useIsLoadingStat } from '../helpers/use-is-loading-stat';
import { formatMinutesToHoursAndMinutes } from '@/utils/dates';
import IconPlayGradient from '@ui/icons/gradient-icons/icon-play-gradient';
import IconAttendeeGradient from '@ui/icons/gradient-icons/icon-attendee-gradient';
import IconStarGradient from '@ui/icons/gradient-icons/icon-star-gradient';
import ViewStatsContent from '../components/view-stats-content';
import useGetStatsIconColors from '../helpers/use-get-stats-icon-colors';
import AuthWrapper from '@ui/auth-wrapper/auth-wrapper';
import { EPermissions } from '@/types/permissions.types';
// icon for "Likes" feature
// import IconTouchAppGradient from '@ui/icons/gradient-icons/icon-touch-app-gradient';

const EventStats = () => {
  const params = useParams();
  const language = params?.language || 'en';
  const projectUuid = params?.projectUuid || '';

  const { data } = useFetchEventOverviewStats({ projectUuid, language });

  const publishedStatus = useAppSelector(getPublishedStatus);

  const isPublished = publishedStatus?.status === EProjectStatus.PUBLIC;

  const isLoadingStat = useIsLoadingStat(data, isPublished);

  const statsColors = useGetStatsIconColors();

  return (
    <Container padding="0 var(--main-content-horizontal-padding)" data-component="EventStats">
      <Container className="margin-b-8 flex ai-center jc-space-between">
        <Text tag="h5" className="H5">
          Stats
        </Text>

        <Container>
          <AuthWrapper allowedRoles={[EPermissions.GroupAdmin]}>
            <Link
              to={`/project/edit/${projectUuid}/${language}/stats`}
              textColor="none"
              focusBackgroundColor="rgba(var(--gray-700-rgb), 0.5)"
              applyFocusStatesToAllChildren
              hoverOpacity={0.8}
            >
              <ViewStatsContent />
            </Link>
          </AuthWrapper>
        </Container>
      </Container>
      <Container className="flex fd-row jc-space-evenly ai-center gap-8">
        <StatsCard
          loading={isLoadingStat(EStatKeys.uniqueAttendees)}
          title="Attendance"
          tooltip="Unique users who have interacted with any session video content across this event."
          value={isPublished ? (data[EStatKeys.uniqueAttendees]?.value ?? -1) : -1}
          Icon={<IconAttendeeGradient from={statsColors.from} to={statsColors.to} />}
        />
        <StatsCard
          loading={isLoadingStat(EStatKeys.avgUserWatchtime)}
          title="Watchtime"
          tooltip="Average duration of video content consumed by attendee"
          value={isPublished ? formatMinutesToHoursAndMinutes(data.avgUserWatchtime?.value ?? 0) : -1}
          Icon={<IconPlayGradient from={statsColors.from} to={statsColors.to} />}
        />
        <StatsCard
          loading={isLoadingStat(EStatKeys.averageProjectRating)}
          title="Ratings"
          tooltip={'Average combined rating of project sessions.'}
          value={isPublished ? (data[EStatKeys.averageProjectRating]?.value ?? -1) : -1}
          Icon={<IconStarGradient from={statsColors.from} to={statsColors.to} />}
        />
        <StatsCard
          loading={isLoadingStat(EStatKeys.interactions)}
          title="Interactions"
          tooltip="Page views."
          value={isPublished ? (data[EStatKeys.interactions]?.value ?? -1) : -1}
          Icon={<IconFilterAltGradient from={statsColors.from} to={statsColors.to} />}
        />
        {/* "Likes" feature doesn't exist yet. */}
        {/* <StatsCard
          loading={isLoadingStat(EStatKeys.registrations)}
          title="Likes"
          tooltip="TODO: Actual numbers not available yet"
          value={isPublished ? (data.registrations?.value ?? -1) : -1}
          Icon={<IconTouchAppGradient />}
        /> */}
        <StatsCard
          loading={isLoadingStat(EStatKeys.shares)}
          title="Shares"
          tooltip="Total unique shares per session per user"
          value={isPublished ? (data[EStatKeys.shares]?.value ?? -1) : -1}
          Icon={<IconSendGradient from={statsColors.from} to={statsColors.to} />}
        />
      </Container>
    </Container>
  );
};

export default EventStats;
